import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Section, Heading, Badge, Button } from '../components/UI.jsx';
import { metadata, studioData } from '../data.js';
import { CheckCircle2, Info, Clock, CreditCard } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils.js';

const Forloeb = () => {
  const priceCategories = [
    {
      title: "EMS TRÆNING",
      items: [
        { name: "Opstartstilbud (Nye kunder)", price: "1500,-", details: "Inkl. 1 x prøvetræning, 4 x EMS & Xbody sæt (Værdi 1950,-)" },
        { name: "Prøvetræning", price: "150,-", details: "Du kan låne et Xbody træningssæt til prøvetræningen" },
        { name: "Enkelt session", price: "400,-", details: "30 min. total (20 min. intensiv træning)" },
        { name: "Klippekort 4 sessions", price: "1400,-", details: "Gyldigt i 3 måneder" },
        { name: "Klippekort 8 sessions", price: "2500,-", details: "Gyldigt i 3 måneder" },
        { name: "Klippekort 10 sessions", price: "3000,-", details: "Gyldigt i 3 måneder" }
      ]
    },
    {
      title: "FORLØB & UDSTYR",
      items: [
        { name: "Forløb: Træn 1 gang pr. uge i 30 dage", price: "1100,-", details: "4 træninger gyldigt i 30 dage" },
        { name: "Forløb: Træn 2 gang pr. uge i 30 dage", price: "2100,-", details: "8 træninger gyldigt i 30 dage" },
        { name: "Xbody træningssæt", price: "400,-", details: "Engangskøb - obligatisk ved start af træningsforløb" },
        { name: "Leje Xbody træningssæt", price: "40,-", details: "Kan lejes til enkelt sessions eller hvis du har glemt din egen dragt" }
      ]
    },
    {
      title: "FYS BEHANDLINGER",
      items: [
        { name: "Fysioterapi", price: "600,-", details: "Session á 45 min. Grundig undersøgelse & behandling" },
        { name: "Sportsmassage", price: "600,-", details: "Session á 45 min. Dybdegående massage" }
      ]
    }
  ];

  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>Priser på EMS Træning &amp; Fysioterapi | LK STUDIO</title>
        <meta name="description" content="Se vores priser på EMS træning, klippekort, personlige forløb og fysioterapi på Frederiksberg. Book en prøvetræning til kun 150 kr. i dag." />
        <link rel="canonical" href="https://lkstudioems.com/priser" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/priser" />
        <meta property="og:title" content="Priser på EMS Træning &amp; Fysioterapi | LK STUDIO" />
        <meta property="og:description" content="Se vores priser på EMS træning, klippekort, personlige forløb og fysioterapi på Frederiksberg. Book en prøvetræning til kun 150 kr. i dag." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/2333d257-a4d9-4629-9e4c-4f90d7df46b9.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/priser" />
        <meta name="twitter:title" content="Priser på EMS Træning &amp; Fysioterapi | LK STUDIO" />
        <meta name="twitter:description" content="Se vores priser på EMS træning, klippekort, personlige forløb og fysioterapi på Frederiksberg. Book en prøvetræning til kun 150 kr. i dag." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/2333d257-a4d9-4629-9e4c-4f90d7df46b9.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-[#F1E5DC] pt-32 lg:pt-44 pb-16 md:pb-24">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Badge className="mb-8">
                Gennemsigtige priser
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Invester i dit <br />
                <span className="text-[#9A8E81] italic">helbred</span>
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#CEBDAA] pt-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    Se vores udvalg af træningsforløb, klippekort og fysioterapeutiske behandlinger. Alle forløb tilpasses dine specifikke behov.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Button 
                      variant="primary" 
                      onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
                    >
                      SE PRISER
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const el = document.querySelector('h2');
                        el?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      LÆS VILKÅR
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle background element */}
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 overflow-hidden opacity-30 lg:opacity-80">
          <div className="relative h-full w-full">
            <img 
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/a344da06-6d31-4787-bb60-24a3e77037af.jpeg", { width: 1200, quality: 80 })} 
              className="w-full h-full object-cover grayscale-[0.4]" 
              alt="LK STUDIO Træningsmiljø" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1E5DC] via-[#F1E5DC]/50 to-transparent" />
          </div>
        </div>
      </section>

      {/* Pricing Tables */}
      <Section className="pt-0">
        <div className="max-w-6xl mx-auto space-y-20">
          {priceCategories.map((category, idx) => (
            <div key={idx}>
              <h2 className="text-xl md:text-2xl font-black uppercase mb-10 border-b border-[#CEBDAA] pb-4 text-[#1C1B1A]">{category.title}</h2>
              <div className="grid gap-6">
                {category.items.map((item, i) => (
                  <motion.div 
                    key={i}
                    whileHover={{ x: 10 }}
                    className="bg-white p-6 md:p-8 rounded-3xl shadow-sm border border-[#CEBDAA]/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
                  >
                    <div>
                      <h4 className="text-lg font-black uppercase mb-2 text-[#1C1B1A]">{item.name}</h4>
                      <p className="text-[#494540] font-medium">{item.details}</p>
                    </div>
                    <div className="flex items-center gap-8">
                      <p className="text-2xl font-black text-[#1C1B1A]">{item.price}</p>
                      <Button variant="primary" className="rounded-xl px-6 sm:px-8" onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}>Book</Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Rules & Terms */}
      <Section className="bg-white pb-12 md:pb-32">
        <div className="max-w-5xl mx-auto bg-[#090807] text-white rounded-[4rem] p-8 md:p-20 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-12 opacity-10">
             <Info className="w-64 h-64" />
          </div>
          
          <div className="relative z-10">
            <h2 className="text-2xl md:text-4xl font-black uppercase mb-12">Regler og vilkår</h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="flex gap-4">
                  <CreditCard className="w-6 h-6 shrink-0 mt-1 text-[#A8814E]" />
                  <div>
                    <h4 className="font-bold uppercase mb-2 text-white">Betaling</h4>
                    <p className="text-[#CEBDAA]/80 font-medium">Sker forud for træningen via MobilePay, bankoverførsel eller kontant ved fremmøde.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock className="w-6 h-6 shrink-0 mt-1 text-[#A8814E]" />
                  <div>
                    <h4 className="font-bold uppercase mb-2 text-white">Afbud</h4>
                    <p className="text-[#CEBDAA]/80 font-medium">Senest 12 timer før. Ved senere afbud eller udeblivelse betales fuld pris (et klip).</p>
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <CheckCircle2 className="w-6 h-6 shrink-0 mt-1 text-[#A8814E]" />
                  <div>
                    <h4 className="font-bold uppercase mb-2 text-white">Gyldighed</h4>
                    <p className="text-[#CEBDAA]/80 font-medium">Klippekort er personlige og refunderes ikke. Bemærk gyldighedsperioder på 3-6 måneder.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Info className="w-6 h-6 shrink-0 mt-1 text-[#A8814E]" />
                  <div>
                    <h4 className="font-bold uppercase mb-2 text-white">Udstyr</h4>
                    <p className="text-[#CEBDAA]/80 font-medium">Eget Xbody træningssæt er obligatorisk ved faste forløb. Kan lejes for 40,- pr. gang.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-16 pt-12 border-t border-white/10 text-center">
              <p className="text-[#9A8E81] font-bold uppercase tracking-widest mb-4">Har du spørgsmål til vores forløb?</p>
              <a href={`mailto:${studioData.email}`} className="text-lg md:text-2xl font-black hover:text-[#A8814E] transition-colors underline decoration-2 underline-offset-8">
                {studioData.email}
              </a>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Forloeb;
